import { IconButton } from '@creditornot/cb-button';
import { DataTable } from '@creditornot/cb-data-table';
import { Trashbin } from '@creditornot/cb-icons';
import { LegacyPopover } from '@creditornot/cb-popover';
import { Spinner } from '@creditornot/cb-progress';
import { InputRadio } from '@creditornot/cb-radio';
import React from 'react';
import { FormattedMessage, IntlShape } from 'react-intl';
import styled from 'styled-components';

import { useRetailContext } from 'common/hooks';

import { LANGUAGE_LABELS } from './constants';
import * as behavioral from '../behavioral';
import { languagesTranslationMap } from '../constants';
import { LanguageStats } from '../types';

type Props = {
  hasPermissionToChangeMenuLanguage: boolean;
  stats: LanguageStats[];
  loading: boolean;
  primaryLanguage: Language;
  pendingPrimaryLanguage: boolean;
  onDeleteLanguage: (language: Language) => void;
  onSetPrimaryLanguage: (language: Language) => void;
  intl: IntlShape;
};

const PrimaryLanguageLoadingSpinner = styled(Spinner).attrs({ size: 'tiny' })`
  margin-inline-start: 8px;
  vertical-align: middle;
`;

const StyledRadio = styled(InputRadio)<{ disabled: boolean }>`
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
`;

const Actions = styled.div`
  position: relative;
`;

const StyledPopover = styled(LegacyPopover).attrs({
  placement: 'left',
  renderArrow: false,
})`
  vertical-align: middle;
`;

const TooltipContent = styled.div`
  padding: 10px;
  max-width: 300px;
`;

const TranslationStats = ({
  hasPermissionToChangeMenuLanguage,
  primaryLanguage,
  pendingPrimaryLanguage,
  stats,
  loading,
  onDeleteLanguage,
  onSetPrimaryLanguage,
  intl,
}: Props) => {
  const isRetailContext = useRetailContext();
  const handleDelete = (languageCode: Language) => (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    behavioral.reportLanguageDeletionStart(languageCode);
    const confirmText = intl.formatMessage(
      { id: 'translation.confirmDelete' },
      {
        lang: intl.formatMessage({ id: languagesTranslationMap[languageCode] }),
      },
    );
    if (window.confirm(confirmText)) {
      behavioral.reportLanguageDeletionConfirmation(languageCode);
      onDeleteLanguage(languageCode);
    } else {
      behavioral.reportLanguageDeletionCancellation(languageCode);
    }
  };

  const handleSetPrimaryLanguage = (language: Language) => () => {
    // Don't allow changing primary language while previous request is pending
    if (pendingPrimaryLanguage) {
      return;
    }

    behavioral.reportPrimaryLanguageChangeStart(language);
    const confirmText = intl.formatMessage({
      id: isRetailContext
        ? 'translation.confirmPrimaryLanguageChangeRetail'
        : 'translation.confirmPrimaryLanguageChange',
    });
    if (window.confirm(confirmText)) {
      behavioral.reportPrimaryLanguageChangeConfirmation(language);
      onSetPrimaryLanguage(language);
    } else {
      behavioral.reportPrimaryLanguageChangeCancellation(language);
    }
  };

  const totalAmount = stats.find(({ language }) => language === primaryLanguage)!.translated;

  return (
    <DataTable<LanguageStats>
      loading={loading}
      columns={[
        {
          id: 'language',
          name: intl.formatMessage({ id: 'translation.language' }),
          render: ({ record: stats }) => <FormattedMessage id={LANGUAGE_LABELS[stats.language]} />,
        },
        {
          id: 'code',
          name: intl.formatMessage({ id: 'translation.code' }),
          render: ({ record: stats }) => stats.language,
        },
        {
          id: 'translated',
          name: intl.formatMessage({ id: 'translation.translated' }),
          render: ({ record: stats }) => (stats.language === primaryLanguage ? totalAmount : stats.translated),
        },
        {
          id: 'missingTranslations',
          name: intl.formatMessage({ id: 'translation.missingTranslations' }),
          render: ({ record: stats }) => (stats.language === primaryLanguage ? 0 : totalAmount - stats.translated),
        },
        {
          id: 'originalChanged',
          name: intl.formatMessage({ id: 'translation.originalChanged' }),
          render: ({ record: stats }) => (stats.language === primaryLanguage ? 0 : stats.unverified),
        },
        {
          id: 'primaryLanguage',
          name: intl.formatMessage({ id: 'translation.primaryLanguage' }),
          render: ({ record: stats }) => {
            const canBeSelectedAsPrimary = totalAmount === stats.translated && stats.unverified === 0;
            return (
              <Actions>
                <StyledPopover
                  disabled={canBeSelectedAsPrimary && hasPermissionToChangeMenuLanguage}
                  content={
                    <TooltipContent>
                      {!hasPermissionToChangeMenuLanguage ? (
                        <FormattedMessage id="translation.noAccessRights" />
                      ) : !canBeSelectedAsPrimary ? (
                        <FormattedMessage id="translation.languageNotSelectableAsPrimary" />
                      ) : undefined}
                    </TooltipContent>
                  }
                >
                  <StyledRadio
                    disabled={!canBeSelectedAsPrimary || !hasPermissionToChangeMenuLanguage}
                    name="primary_language"
                    checked={stats.language === primaryLanguage}
                    onChange={handleSetPrimaryLanguage(stats.language)}
                  />
                </StyledPopover>
                {pendingPrimaryLanguage && <PrimaryLanguageLoadingSpinner />}
              </Actions>
            );
          },
        },
        {
          id: 'actions',
          name: intl.formatMessage({ id: 'translation.actions' }),
          render: ({ record: stats }) =>
            primaryLanguage !== stats.language && (
              <StyledPopover
                disabled={hasPermissionToChangeMenuLanguage}
                content={
                  <TooltipContent>
                    {!hasPermissionToChangeMenuLanguage && <FormattedMessage id="translation.noAccessRights" />}
                  </TooltipContent>
                }
              >
                <IconButton
                  variant="subdued"
                  size="small"
                  onClick={handleDelete(stats.language)}
                  disabled={!hasPermissionToChangeMenuLanguage}
                >
                  <Trashbin />
                </IconButton>
              </StyledPopover>
            ),
        },
      ]}
      getRowKey={row => row.language}
      dataSource={stats}
    />
  );
};

export default TranslationStats;
